import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { HQ_PERMISSION_LEVELS } from 'src/constants';
import { useSession } from 'src/contexts/SessionContext';
import {
  fetchChartBlueprints,
  fetchKpiBlueprints,
} from 'src/models/business-report/business-report';
import { DashboardLayoutObject } from 'src/models/dashboard/_types';
import { fetchReportOptions } from 'src/models/report/report';
import { fetchTrainerEmails } from 'src/models/user/user';
import { getHqPermissionLevel } from 'src/shared/HQ/utils';

export const useTrainerSettings = () => {
  const { session: currentPlatform, trainer: currentTrainer } = useSession();
  const { t } = useTranslation('translation', {
    keyPrefix: 'staff.settings',
  });
  const { t: tTrainer } = useTranslation('translation', {
    keyPrefix: 'trainer',
  });

  const { data: emailOptions = [] } = useQuery({
    queryKey: ['emailOptions'],
    queryFn: fetchTrainerEmails,
  });

  const { data: allReports = [] } = useQuery({
    queryKey: ['allReports'],
    queryFn: fetchReportOptions,
  });

  const { data: serverKpiBlueprints, isLoading: kpiBlueprintsLoading } =
    useQuery({
      queryKey: ['kpiBlueprints'],
      queryFn: fetchKpiBlueprints,
    });

  // Add special KPI cases for permissions
  const kpiBlueprints = useMemo(() => {
    const specialKpis: DashboardLayoutObject[] = [
      {
        id: 'livestream',
        name: 'Livestream',
        valueType: 'number',
        description: 'Real-time sales and activity stream',
        icon: 'stream',
        dataType: 'kpi',
        key: 'livestream',
        custom: true,
      },
      {
        id: 'breakdown',
        name: 'Breakdown',
        valueType: 'number',
        description: 'Summary breakdown of key metrics',
        icon: 'chart-pie',
        dataType: 'kpi',
        key: 'breakdown',
        custom: true,
      },
    ];

    return [
      ...((serverKpiBlueprints as DashboardLayoutObject[]) || []),
      ...specialKpis,
    ];
  }, [serverKpiBlueprints]);

  const { data: chartBlueprints, isLoading: chartBlueprintsLoading } = useQuery(
    {
      queryKey: ['chartBlueprints'],
      queryFn: fetchChartBlueprints,
    }
  );

  const additionalPermissions = useMemo(() => {
    return [
      { id: 0, name: t('canManageAll') },
      { id: 1, name: t('canManageTheirOwn') },
      { id: 2, name: t('canManageTheirOwnAndViewOthers') },
      { id: 3, name: t('canManageTheirOwnAndViewAdmins') },
      {
        id: 4,
        name: t('notVisibleToSubTrainer', {
          subTrainer: tTrainer('one').toLowerCase(),
        }),
      },
    ];
  }, [t, tTrainer]);

  const locationPermissions = useMemo(() => {
    return [
      { id: 0, name: t('canManageAll') },
      { id: 1, name: t('canManageTheirOwn') },
      { id: 3, name: t('canManageTheirLocation') },
      {
        id: 2,
        name: t('notVisibleToSubTrainer', {
          subTrainer: tTrainer('one').toLowerCase(),
        }),
      },
    ];
  }, [t, tTrainer]);

  const connectedAppsPermissions = useMemo(() => {
    return [
      {
        id: 0,
        name: t('canManageAdminAccount'),
      },
      { id: 1, name: t('canManageTheirOwn') },
      {
        id: 2,
        name: t('notVisibleToSubTrainer', {
          subTrainer: tTrainer('one').toLowerCase(),
        }),
      },
    ];
  }, [t, tTrainer]);

  const fbmPermissions = useMemo(() => {
    return [
      { id: 0, name: t('canManageEverything') },
      { id: 1, name: t('canManageTheirOwnAndViewOthers') },
      { id: 5, name: t('canOnlyManageAndViewTheirOwn') },
      {
        id: 2,
        name: t('canManageSchedulingButNotPackagesLocationsServices'),
      },
      {
        id: 4,
        name: t('canOnlyViewTheSubTrainerSchedule', {
          subTrainer: tTrainer('one').toLowerCase(),
        }),
      },
      {
        id: 3,
        name: t('notVisibleToSubTrainer', {
          subTrainer: tTrainer('one').toLowerCase(),
        }),
      },
    ];
  }, [t, tTrainer]);

  const permissions = useMemo(() => {
    return [
      { id: 0, name: t('canManageAll') },
      { id: 1, name: t('canManageTheirOwn') },
      {
        id: 2,
        name: t('notVisibleToSubTrainer', {
          subTrainer: tTrainer('one').toLowerCase(),
        }),
      },
    ];
  }, [t, tTrainer]);

  const cancelVisitPermissions = useMemo(() => {
    return [
      { id: 0, name: t('canCancelVisits') },
      { id: 1, name: t('canNotOverrideLateCancel') },
      { id: 2, name: t('canNotCancelVisits') },
    ];
  }, [t]);

  const clientPackagePermissions = useMemo(() => {
    return [
      {
        id: 0,
        name: t('canEditClientMemberships'),
      },
      {
        id: 1,
        name: t('cannotEditClientMemberships'),
      },
    ];
  }, [t]);

  const subTrainerPermissionOptions = useMemo(() => {
    return [
      { id: 0, name: t('canManageAll') },
      {
        id: 1,
        name: t('canManageOtherSubTrainersButNotEditPermissions', {
          subTrainer: tTrainer('one').toLowerCase(),
        }),
      },
      {
        id: 2,
        name: t('notVisibleToSubTrainer', {
          subTrainer: tTrainer('one').toLowerCase(),
        }),
      },
    ];
  }, [t, tTrainer]);

  const clockPermissionOptions = useMemo(() => {
    return [
      { id: 0, name: t('canManageAll') },
      { id: 1, name: t('canViewOthersAndClockInOut') },
      { id: 2, name: t('canClockInOut') },
      { id: 3, name: t('cannotClockInOut') },
    ];
  }, [t]);

  const inventoryPermissionOptions = useMemo(() => {
    // Can view inventory for my Location (default)
    // Can adjust inventory for my Location (includes manual edits, POS stock overrides)
    // Can view and adjust inventory for ALL locations
    // Cannot view inventory
    return [
      { id: 0, name: t('canManageAll') },
      { id: 1, name: t('canAdjustInventoryForMyLocation') },
      { id: 2, name: t('canViewInventoryForMyLocation') },
      { id: 3, name: t('cannotViewInventory') },
    ];
  }, [t]);

  const billingInfoPermissionOptions = useMemo(() => {
    return [
      { id: 0, name: t('canViewBillingInfo') },
      { id: 1, name: t('cannotViewBillingInfo') },
    ];
  }, [t]);

  const clientManagementTabs = useMemo(() => {
    return currentPlatform?.platformTabs?.clientManagement || [];
  }, [currentPlatform]);

  const dashboardBoxes = useMemo(() => {
    return [
      { value: 'Notifications', name: t('notifications') },
      { value: 'Activity', name: t('activity') },
      {
        value: 'Clients',
        name: tTrainer('client.other'),
      },
      { value: 'Workouts', name: t('workouts') },
      { value: 'Messages', name: t('messages') },
      { value: 'Business', name: t('business') },
      { value: 'Actions', name: t('actions') },
      { value: 'Charges', name: t('charges') },
      { value: 'Past Due Subscriptions', name: t('pastDueSubscriptions') },
      { value: 'Upcoming Charges', name: t('upcomingCharges') },
      { value: 'Measurements', name: t('measurements') },
      { value: 'Schedule', name: t('schedule') },
    ];
  }, [t, tTrainer]);

  const dashboardNotifications = useMemo(() => {
    return [
      {
        value: 'New Clients',
        name: t('newClients'),
      },
      { value: 'Failed Payments', name: t('failedPayments') },
      { value: 'No Logged Workout', name: t('noLoggedWorkout') },
      {
        value: 'Expiring Package',
        name: t('expiringPackage'),
      },
      { value: 'Expiring Card', name: t('expiringCard') },
      {
        value: 'Completed Assessment',
        name: t('completedAssessment'),
      },
      { value: 'Birthday', name: t('birthday') },
      { value: 'No Recent Visit', name: t('noRecentVisit') },
      { value: 'Calendar Plan Ending', name: t('calendarPlanEnding') },
      { value: 'Calendar Ending', name: t('calendarEnding') },
      { value: 'New Uploads', name: t('newUploads') },
      { value: 'New Messages', name: t('newMessages') },
    ];
  }, [t]);

  const adminEmails = useMemo(() => {
    return [
      { value: 'billing_succeeded', name: t('paymentSucceeded') },
      { value: 'billing_failed', name: t('paymentFailed') },
      { value: 'subscription_cancelled', name: t('subscriptionCancelled') },
      {
        value: 'fbm_package_sign_up',
        name: t('packagePurchased'),
      },
      {
        value: 'completed_assessment',
        name: t('assessmentCompleted'),
      },
    ];
  }, [t]);

  const posConfigurationPermissions = useMemo(() => {
    return [
      {
        id: 0,
        name: t('posRoleYes'),
      },
      { id: 1, name: t('posRoleOnlyForMyLocation') },
      { id: 2, name: t('posRoleNo') },
    ];
  }, [t]);

  const defaultPermissions = useMemo(() => {
    return {
      assessmentsRole: 0,
      schedulesRole: 0,
      messagesRole: 1,
      resourcesRole: 0,
      videosRole: 0,
      routinesRole: 0,
      subtrainerRole: 0,
      clockRole: 0,
      connectedAppsRole: 0,
      workoutPlansRole: 0,
      exercisesRole: 0,
      clientsRole: 0,
      groupsRole: 0,
      schedulingRole: 0,
      clientPackagesRole: 0,
      cancelVisitsRole: 0,
      posRole: 0,
      inventoryRole: 0,
      canViewBillingInfoRole: 0,
      disabledTabs: [],
      disabledDashboardBoxes: [],
      disabledDashboardNotifications: [],
      disabledEmails: [],
      disabledDashboardKpis: [],
      disabledDashboardCharts: [],
      disabledTasks: [],
      reportsRole: [],
      headTrainerEmails: [],
    };
  }, []);

  const defaultHQPermissions = useMemo(() => {
    // If current user has staff permission level 1, set defaults to level 1
    // Otherwise, set defaults to level 0 (full permissions)
    const currentUserStaffLevel = getHqPermissionLevel(currentTrainer, 'staff');
    const defaultLevel =
      currentUserStaffLevel === HQ_PERMISSION_LEVELS.MANAGE_MY_LOCATIONS
        ? HQ_PERMISSION_LEVELS.MANAGE_MY_LOCATIONS
        : HQ_PERMISSION_LEVELS.MANAGE_ALL;

    return {
      hqPermissions: {
        dashboards: { level: defaultLevel },
        reports: { level: defaultLevel },
        sites: { level: defaultLevel },
        dataLoad: { level: defaultLevel },
        dataLoadSend: { level: defaultLevel },
        staff: { level: defaultLevel },
        corporateSettings: { level: defaultLevel },
        loginToSites: { level: defaultLevel },
      },
      reportsRole: [],
    };
  }, [currentTrainer]);

  return {
    emailOptions,
    additionalPermissions,
    locationPermissions,
    connectedAppsPermissions,
    fbmPermissions,
    permissions,
    cancelVisitPermissions,
    clientPackagePermissions,
    subTrainerPermissionOptions,
    clockPermissionOptions,
    inventoryPermissionOptions,
    billingInfoPermissionOptions,
    clientManagementTabs,
    dashboardBoxes,
    dashboardNotifications,
    adminEmails,
    allReports,
    defaultPermissions,
    posConfigurationPermissions,
    defaultHQPermissions,
    kpiBlueprints,
    chartBlueprints,
    kpiBlueprintsLoading,
    chartBlueprintsLoading,
  };
};
